import { Component, computed, effect, ElementRef, inject, output, signal, untracked, viewChildren } from '@angular/core';
import { BookmarksService } from '../../services/bookmarks.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { getAssetURL } from 'src/app/constants/assets';
import { SkuMapProviderService } from 'src/app/providers/sku-map/sku-map.provider.service';
import { assert } from 'src/app/utils/assert.util';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { Navigation2Service } from '../../services/navigation2.service';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { Bookmark } from 'src/app/interfaces/bookmark';
import { MatDialog } from '@angular/material/dialog';
import { DenyVisibilityDialogComponent } from '../deny-visibility.dialog/deny-visibility.dialog.component';
import { environment } from 'src/environments/environment';
import { PriceService } from 'src/app/services/price/price.service';
import { CheckoutService } from '../../services/checkout.service';
import { ConfirmBookmarkDeleteDialogComponent } from '../confirm-bookmark-delete.dialog/confirm-bookmark-delete.dialog.component';
import { Price } from 'src/app/interfaces/Price';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, Subscription } from 'rxjs';
import { ConfirmClearBookmarksDialogComponent } from '../confirm-clear-bookmarks.dialog/confirm-clear-bookmarks.dialog.component';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { Size } from 'src/app/interfaces/size';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneCountsProviderService } from 'src/app/providers/stone-counts/stone-counts.provider.service';

@Component({
  selector: 'app-bookmarks',
  templateUrl: './bookmarks.component.html',
  styleUrl: './bookmarks.component.scss'
})
export class BookmarksComponent {
  private stoneCountsProvider = inject(StoneCountsProviderService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private priceService = inject(PriceService);
  private bookmarksService = inject(BookmarksService);
  private configuredItemService = inject(ConfiguredItemService);
  private skuMapProvider = inject(SkuMapProviderService);
  private loadingService = inject(LoadingService);
  private navigationService = inject(Navigation2Service);
  private selectedAttributeService = inject(SelectedAttributeService);
  private matDialog = inject(MatDialog);
  private checkoutService = inject(CheckoutService);
  private bookmarksSubscription: Subscription;
  private bookmarkElementRefs = viewChildren('bookmark', { read: ElementRef<HTMLDivElement> });
  isRetailerMode = computed(() => this.checkoutService.isRetailerOnly());
  shouldShowWholesalePrice = computed(() => !this.checkoutService.b2bData.retailPricingOnly);
  didCompleteInitialLoad = signal(false);
  bookmarksToShow = computed(async () => {
    this.loadingService.load('bookmarks::bookmarks-to-show');
    const bookmarksToShow = await Promise.all(this.bookmarksService.bookmarks().slice().reverse().map(async (bookmark) => {
      const [customSKU, haloSKU] = bookmark.sku.split(':').filter(token => token.length > 0);
      const {size} = this.configuredItemService.getAttributesFromNewSKU(customSKU);
      assert(size, 'No size in sku');
      const haloStyle = haloSKU?.replace(/\d{2}\.?\d?$/, '');
      const skuToShow = haloStyle ? this.configuredItemService.buildHaloSKU(haloStyle, size, false) : customSKU;
      const configuredItem = this.configuredItemService.getAttributesFromNewSKU(customSKU);
      assert(configuredItem.size, 'No size for bookmark');
      try {
        const { price, retailPrice } = await this.getPrices(configuredItem, haloStyle);
        assert(retailPrice, 'No retail price for bookmark');
        const bookmarkToShow: Bookmark = { key: bookmark.sku, sku: customSKU, haloStyle: haloStyle ?? null, skuToShow, wholesalePrice: !this.checkoutService.ringBuilderID ? undefined : price.value, retailPrice: retailPrice, attributes: configuredItem, imageData: bookmark.imageData };
        return bookmarkToShow;
      } catch(e) {
        console.log(`Not showing bbookmark ${bookmark}`);
        console.error(e);
        return null;
      }
    }));
    this.loadingService.done('bookmarks::bookmarks-to-show');
    return bookmarksToShow.filter(b => !!b);
  });
  visibleBookmarks = signal(new Map<string, Bookmark>(), { equal: () => false });
  gemstoneBookmarkLabels = signal(new Map<string, any>(), { equal: () => false });
  stoneCounts = signal<Map<string, number> | null>(null);

  constructor() {
    effect(async () => {
      this.loadingService.load("Bookmarks::LoadStoneCounts");
      const bookmarksToShow = await this.bookmarksToShow();
      const stoneCounts = await Promise.all(bookmarksToShow.filter(b => !!b.attributes.gemstoneType).map(async b => ( { key: b.key, count: await this.stoneCountsProvider.get(b.attributes) })));
      this.stoneCounts.set(stoneCounts.reduce((map, c) => {
        map.set(c.key, c.count);
        return map;
      }, new Map<string, number>()));
      this.loadingService.done("Bookmarks::LoadStoneCounts");
    }, { allowSignalWrites: true });

     this.bookmarksSubscription = toObservable(this.bookmarksToShow).subscribe((bookmarks) => {
       this.bookmarksSubscription.unsubscribe();
      (async () => {
        const bookmarksValue = await bookmarks;
        const bookmarksToMakeVisible = bookmarksValue.slice(0,2);
        if (bookmarksToMakeVisible.length > 0) {
          this.visibleBookmarks.update(v => {
            bookmarksToMakeVisible.forEach(bookmark => {
              v.set(bookmark.sku, bookmark);
            })
            return v;
          });
        }
        this.didCompleteInitialLoad.set(true);
      })();
     });

     effect(() => {
      this.bookmarksService.skusToCompare.set(Array.from(this.visibleBookmarks().keys()));
     }, { allowSignalWrites: true });

     effect(() => {
        const selectedBookmarkKey = this.bookmarksService.selectedBookmarkIndex();
        if (selectedBookmarkKey) {
          const visibleBookmark = this.visibleBookmarks().get(selectedBookmarkKey);
          const bookmarkRef = this.bookmarkElementRefs().find(ref => ref.nativeElement.getAttribute('bookmark-key') === visibleBookmark?.key);
          bookmarkRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
        }
     });

    effect(async () => {
      this.loadingService.load("Bookmarks::LoadStoneBookmarkLabels");
      const stoneCounts = this.stoneCounts();
      const bookmarksToShow = await this.bookmarksToShow();
      if (stoneCounts) {
        const stoneInfo = bookmarksToShow.filter(b => !!b.attributes.gemstoneType).map(b => ( { key: b.key, attributes: b.attributes, count: stoneCounts?.get(b.key) }));
        this.gemstoneBookmarkLabels.update(gemstoneBookmarkLabelsMap => {
          stoneInfo.forEach(c => {
            const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(aws => aws.stoneArrangementID === this.selectedAttributeService.configuredItem().gemstoneArrangement?.id && aws.stoneSettingID === this.selectedAttributeService.configuredItem().gemstoneSetting?.id);
            const typeWithShape = this.stoneTypesWithShapesService.values().find(tws => tws.stoneTypeID === this.selectedAttributeService.configuredItem().gemstoneType?.id && this.selectedAttributeService.configuredItem().gemstoneShape?.id === this.selectedAttributeService.configuredItem().gemstoneShape?.id);
            const label = [`${c.count} x ${this.selectedAttributeService.configuredItem().gemstoneSize?.displayName}`, `${typeWithShape?.displayName}`, `${arrangementWithSetting?.displayName}`];
            gemstoneBookmarkLabelsMap.set(c.key, label);
          });
          return gemstoneBookmarkLabelsMap;
        });
      }
      this.loadingService.done("Bookmarks::LoadStoneBookmarkLabels");
    }, { allowSignalWrites: true });
  }

  ngOnDestroy() {
    this.bookmarksSubscription.unsubscribe();
  }

  async getPrices(configuredItem: ConfiguredItem, haloStyle: string) {
    const size = configuredItem.size;
    assert(size, 'No size for bookmark');
    if (!haloStyle) {
      const price = await this.getConfiguredItemPrice(configuredItem, size);
      const retailPrice = this.getRetailPrice(price, true, configuredItem);
      return { price, retailPrice };
    }

    try {
      const price = await this.priceService.getPriceWithHaloStyle(haloStyle, size.value, this.checkoutService.isRetailerOnly())
      const retailPrice = this.getRetailPrice(price, false, configuredItem);
      return { price, retailPrice };
    } catch {
      const price = await this.getConfiguredItemPrice(configuredItem, size);
      const retailPrice = this.getRetailPrice(price, true, configuredItem);
      return { price, retailPrice };
    }
  }

  private async getConfiguredItemPrice(configuredItem: ConfiguredItem, size: Size) {
      const price = await this.priceService.getPrice(configuredItem, size.value);
      return price;
  }

  private getRetailPrice(wholesalePrice: Price | undefined, shouldUseCustomMarkups: boolean, configuredItem: ConfiguredItem) {
    if (this.checkoutService.isRetailerOnly() && wholesalePrice) {
      return this.priceService.computeRetailerPrice(wholesalePrice.value, configuredItem);
    }

    if (wholesalePrice) {
      return this.priceService.computeRetailPriceFromWholesale(wholesalePrice, shouldUseCustomMarkups);
    }

    return undefined;
  }

  async getCustomizerStyle(sku: string) {
    const style = sku.replace(/\d{2}\.?\d?$/, '');
    const skuMapEntry = await this.skuMapProvider.get(style);
    return skuMapEntry?.ringBuilderSKU;
  }

  getThumbnail(sku: string) {
    const style = this.configuredItemService.getStyleFromCustomSKU(sku).replace(/PT/g, 'W');
    return `${getAssetURL(`sm/${style}_P1.jpg`, 'webimages')}`;
  }

  async editClick(bookmark: Bookmark) {
    this.loadingService.load('bookmarks::Review-click');
    await this.selectedAttributeService.setConfiguredItemFromSKU(bookmark.sku, (b2bdata) => {
      b2bdata.haloStyle = bookmark.haloStyle ?? undefined;
      b2bdata.selectedHaloStyleNumber = undefined;
      const size = bookmark.attributes.size;
      assert(size, 'Size was null');
      const customStyle = this.configuredItemService.getStyleFromCustomSKU(bookmark.sku);
      this.selectedAttributeService.pricingObject.set({ style: bookmark.haloStyle ?? customStyle, size });
    });
    this.selectedAttributeService.undoList.set([]);
    this.selectedAttributeService.redoList.set([]);
    this.navigationService.navigateTo(this.navigationService.views().findIndex(v => v.title === 'Design'));
    this.loadingService.done('bookmarks::Review-click');
  }

  removeClick(bookmark: Bookmark) {
    this.matDialog.open(ConfirmBookmarkDeleteDialogComponent).afterClosed().subscribe(async didConfirm => {
      if (didConfirm) {
        this.loadingService.load('bookmarks::removing');
        await this.bookmarksService.remove(bookmark.key);
        const existingVisibleBookmark = this.visibleBookmarks().get(bookmark.sku);
        if (existingVisibleBookmark) {
          this.visibleBookmarks.update(v => {
            v.delete(bookmark.sku);
            return v;
          });
        }
        this.loadingService.done('bookmarks::removing');
      }
    });
  }

  toggleVisible(bookmark: Bookmark) {
    const existingVisibleBookmark = this.visibleBookmarks().get(bookmark.sku);
    if (existingVisibleBookmark) {
      this.visibleBookmarks.update(v => {
        v.delete(bookmark.sku);
        return v;
      });
    } else {
      if (this.visibleBookmarks().size + 1 <= (environment.maxCompareVisibilityCount ?? 3)) {
        this.visibleBookmarks.update(v => {
          v.set(bookmark.sku, bookmark);
          return v;
        });
      } else {
        this.matDialog.open(DenyVisibilityDialogComponent);
      }
    }
  }

  getMaterials(bookmark: Bookmark) {
    if (bookmark.attributes.secondaryMaterial?.displayName === bookmark.attributes.primaryMaterial?.displayName || !bookmark.attributes.secondaryMaterial?.displayName) {
      return bookmark.attributes.primaryMaterial?.displayName;
    } else {
      return `${bookmark.attributes.primaryMaterial?.displayName}/${bookmark.attributes.secondaryMaterial?.displayName}`;
    }
  }

  getVisibilityLabel(bookmark: Bookmark) {
    const visibleKeys = Array.from(this.visibleBookmarks().keys());
    const bookmarkIndex = visibleKeys.findIndex((k) => bookmark.sku === k);
    if (bookmarkIndex >= 0) {
      return String.fromCharCode(65 + bookmarkIndex);
    }
    return null;
  }

  clearAllClicked() {
    this.matDialog.open(ConfirmClearBookmarksDialogComponent).afterClosed().subscribe(async shouldClear => {
      if (shouldClear) {
        this.loadingService.load('bookmarks::clear');
        await this.bookmarksService.clear();
        this.visibleBookmarks.set(new Map());
        this.loadingService.done('bookmarks::clear');
      }
    });
  }
}
